import React, { useReducer, useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/Common';
import { DndContext, DragOverlay, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RichTextEditor from '../components/templateEditor/RichTextEditor';

function generateId(prefix = 'sec') {
  return prefix + '_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
}

const SOW_SECTION_TYPES = [
  { type: 'sow-header', label: 'Header (Logo + Address)', icon: '🏢' },
  { type: 'sow-title', label: 'Title', icon: '📄' },
  { type: 'sow-preamble', label: 'Preamble', icon: '📝' },
  { type: 'sow-term', label: 'Term / Dates', icon: '📅' },
  { type: 'sow-representatives', label: 'Representatives', icon: '👥' },
  { type: 'sow-overview', label: 'Overview', icon: '📋' },
  { type: 'sow-services', label: 'Summary of Services', icon: '🔧' },
  { type: 'sow-scope-table', label: 'Scope Table', icon: '🎯' },
  { type: 'sow-credit-table', label: 'Credit Allocation', icon: '💳' },
  { type: 'sow-assumptions', label: 'Assumptions', icon: '📌' },
  { type: 'sow-deliverables', label: 'Deliverables', icon: '📦' },
  { type: 'sow-remediation', label: 'Remediation Validation', icon: '🔄' },
  { type: 'sow-risk-statement', label: 'Statement of Risk', icon: '⚠' },
  { type: 'sow-fees', label: 'Fees', icon: '💰' },
  { type: 'sow-signature', label: 'Signature Block', icon: '✍' },
  { type: 'heading', label: 'Heading', icon: 'H' },
  { type: 'rich-text', label: 'Rich Text', icon: '¶' },
  { type: 'page-break', label: 'Page Break', icon: '—' },
];

const DEFAULT_CONFIGS = {
  'sow-header': { tagline: 'Penetration Testing', address: 'TSR Solutions, Inc.' },
  'sow-title': { titleText: 'Statement of Work' },
  'sow-preamble': { html: '<p>This Statement of Work ("SoW") is entered into as of %SOWDATE% by and between %PROVIDERNAME% ("Provider") and %CLIENTNAME% ("Client") in connection with the Master Services Agreement between the parties.</p>' },
  'sow-term': { html: '<p>This SoW shall commence on %STARTDATE% and terminate on %ENDDATE%, unless earlier terminated in accordance with the Master Services Agreement.</p>' },
  'sow-representatives': { html: '<p><strong>Provider Representative</strong><br/>%PROVIDERCONTACT%<br/>%PROVIDEREMAIL%<br/>%PROVIDERPHONE%</p><p><strong>Client Representative</strong><br/>%CLIENTCONTACT%<br/>%CLIENTEMAIL%<br/>%CLIENTPHONE%</p>' },
  'sow-overview': { html: '<p>%PROVIDERNAME% will perform penetration testing services for %CLIENTNAME% as described in this Statement of Work. Testing follows the Penetration Testing Execution Standard (PTES) framework and OWASP Testing Guide.</p>' },
  'sow-services': { webApp: true, api: true, mobile: false, network: false, pci: false },
  'sow-scope-table': {},
  'sow-credit-table': {},
  'sow-assumptions': { html: '<ul><li>Testing will be conducted during normal business hours unless otherwise agreed.</li><li>Client will provide necessary access and credentials prior to testing start date.</li><li>A detailed report will be delivered within 5 business days of testing completion.</li><li>Remediation validation will be performed within 30 days of report delivery at no additional cost.</li></ul>' },
  'sow-deliverables': { html: '<ul><li>Executive Summary Report</li><li>Detailed Technical Findings Report</li><li>Remediation Validation (one round within 30 days)</li><li>Letter of Attestation (upon request)</li></ul>' },
  'sow-remediation': { html: '<p>Remediation validation testing will be provided at no additional cost within 30 calendar days of report delivery. This includes one round of retesting for all identified vulnerabilities.</p>' },
  'sow-risk-statement': { html: '<p>Penetration testing by nature involves techniques that could potentially disrupt services. While %PROVIDERNAME% takes every precaution to minimize risk, there is an inherent possibility of service disruption during testing activities. %PROVIDERNAME% will coordinate closely with %CLIENTNAME% and cease testing immediately upon request.</p>' },
  'sow-fees': {},
  'sow-signature': {},
  'heading': { heading: 'Section Heading', level: 2 },
  'rich-text': { heading: '', html: '', pageBreakBefore: false },
  'page-break': {},
};

const SOW_VARIABLES = [
  { var: '%CLIENTNAME%', desc: 'Client name' },
  { var: '%CLIENTCONTACT%', desc: 'Client contact person' },
  { var: '%CLIENTEMAIL%', desc: 'Client email' },
  { var: '%CLIENTPHONE%', desc: 'Client phone' },
  { var: '%PROVIDERNAME%', desc: 'Provider name (TSR)' },
  { var: '%PROVIDERCONTACT%', desc: 'TSR contact person' },
  { var: '%PROVIDEREMAIL%', desc: 'TSR email' },
  { var: '%PROVIDERPHONE%', desc: 'TSR phone' },
  { var: '%SOWNUMBER%', desc: 'SoW number' },
  { var: '%PONUMBER%', desc: 'Purchase order' },
  { var: '%STARTDATE%', desc: 'Start date' },
  { var: '%ENDDATE%', desc: 'End date' },
  { var: '%SOWDATE%', desc: 'SoW creation date' },
  { var: '%CREDITCOUNT%', desc: 'Number of credits' },
  { var: '%PRICEPERUNIT%', desc: 'Price per unit' },
  { var: '%TOTALPRICE%', desc: 'Total price' },
  { var: '%APPLICATIONCOUNT%', desc: 'Number of apps' },
];

function templateReducer(state, action) {
  switch (action.type) {
    case 'SET_TEMPLATE': return action.payload;
    case 'ADD_SECTION': {
      const newSection = {
        id: generateId(),
        type: action.payload.type,
        label: action.payload.label,
        config: { ...(DEFAULT_CONFIGS[action.payload.type] || {}) }
      };
      const sections = [...(state.sections || [])];
      if (action.payload.index != null) sections.splice(action.payload.index, 0, newSection);
      else sections.push(newSection);
      return { ...state, sections };
    }
    case 'REMOVE_SECTION': return { ...state, sections: (state.sections || []).filter(s => s.id !== action.payload) };
    case 'REORDER_SECTIONS': return { ...state, sections: action.payload };
    case 'UPDATE_SECTION': return {
      ...state,
      sections: (state.sections || []).map(s => s.id === action.payload.id ? { ...s, ...action.payload.updates } : s)
    };
    case 'UPDATE_STYLES': return { ...state, styles: { ...state.styles, ...action.payload } };
    default: return state;
  }
}

const INITIAL_STATE = {
  version: 1,
  pageSettings: { size: 'Letter', margins: { top: 72, bottom: 72, left: 72, right: 72 } },
  styles: { headingFont: 'Calibri', bodyFont: 'Calibri', headingSize: 14, bodySize: 11, primaryColor: '#1a2030', bodyAlignment: 'left' },
  sections: []
};

function SortableSection({ section, onUpdate, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const [collapsed, setCollapsed] = useState(false);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    marginBottom: 8,
  };

  const sectionInfo = SOW_SECTION_TYPES.find(t => t.type === section.type) || { icon: '?', label: section.type };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        cursor: 'grab', borderBottom: collapsed ? 'none' : '1px solid var(--border)',
        userSelect: 'none',
      }} {...attributes} {...listeners}>
        <span style={{ fontSize: 14, opacity: 0.5, cursor: 'grab' }}>☰</span>
        <span style={{ fontSize: 14 }}>{sectionInfo.icon}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{section.label || sectionInfo.label}</span>
        <button onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'inherit' }}>
          {collapsed ? '▸' : '▾'}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onRemove(section.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#ef4444', fontFamily: 'inherit' }}>
          ×
        </button>
      </div>
      {!collapsed && (
        <div style={{ padding: 12 }}>
          <SectionConfig section={section} onUpdate={onUpdate} />
        </div>
      )}
    </div>
  );
}

function SectionConfig({ section, onUpdate }) {
  const update = (key, val) => onUpdate(section.id, { config: { ...section.config, [key]: val } });
  const config = section.config || {};

  const labelInput = (
    <div style={{ marginBottom: 8 }}>
      <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Label</label>
      <input value={section.label || ''} onChange={e => onUpdate(section.id, { label: e.target.value })}
        style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-input)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit' }} />
    </div>
  );

  switch (section.type) {
    case 'sow-header':
      return (<div>{labelInput}
        <Field label="Tagline" value={config.tagline} onChange={v => update('tagline', v)} />
        <Field label="Address" value={config.address} onChange={v => update('address', v)} />
      </div>);
    case 'sow-title':
      return (<div>{labelInput}
        <Field label="Title Text" value={config.titleText} onChange={v => update('titleText', v)} />
      </div>);
    case 'sow-services':
      return (<div>{labelInput}
        {['webApp', 'api', 'mobile', 'network', 'pci'].map(key => (
          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={config[key] || false} onChange={e => update(key, e.target.checked)} />
            {key === 'webApp' ? 'Web Application' : key === 'api' ? 'API' : key === 'pci' ? 'PCI DSS' : key.charAt(0).toUpperCase() + key.slice(1)}
          </label>
        ))}
      </div>);
    case 'heading':
      return (<div>{labelInput}
        <Field label="Heading Text" value={config.heading} onChange={v => update('heading', v)} />
        <div style={{ marginBottom: 8 }}>
          <label style={fieldLabelStyle}>Level</label>
          <select value={config.level || 2} onChange={e => update('level', Number(e.target.value))}
            style={selectStyle}>
            <option value={1}>H1</option><option value={2}>H2</option><option value={3}>H3</option>
          </select>
        </div>
      </div>);
    case 'sow-preamble':
    case 'sow-term':
    case 'sow-overview':
    case 'sow-assumptions':
    case 'sow-deliverables':
    case 'sow-remediation':
    case 'sow-risk-statement':
    case 'rich-text':
      return (<div>{labelInput}
        {config.heading !== undefined && <Field label="Heading" value={config.heading} onChange={v => update('heading', v)} />}
        <div style={{ marginBottom: 8 }}>
          <label style={fieldLabelStyle}>Content</label>
          <RichTextEditor value={config.html || ''} onChange={v => update('html', v)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={config.pageBreakBefore || false} onChange={e => update('pageBreakBefore', e.target.checked)} />
          Page break before
        </label>
      </div>);
    case 'sow-representatives':
      return (<div>{labelInput}
        <div style={{ marginBottom: 8 }}>
          <label style={fieldLabelStyle}>Content</label>
          <RichTextEditor value={config.html || ''} onChange={v => update('html', v)} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={config.pageBreakBefore || false} onChange={e => update('pageBreakBefore', e.target.checked)} />
          Page break before
        </label>
      </div>);
    case 'sow-scope-table':
    case 'sow-credit-table':
    case 'sow-fees':
    case 'sow-signature':
      return (<div>{labelInput}
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', padding: '8px 0' }}>
          This section is auto-populated from the wizard data when generating a SoW.
        </div>
      </div>);
    case 'page-break':
      return <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>Page break — no configuration needed.</div>;
    default:
      return <div>{labelInput}</div>;
  }
}

const fieldLabelStyle = { fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: 2 };
const selectStyle = { width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-input)', color: 'var(--text)', fontFamily: 'inherit' };

function Field({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <label style={fieldLabelStyle}>{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-input)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit' }} />
    </div>
  );
}

export default function SowTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiFetch } = useAuth();
  const [template, dispatch] = useReducer(templateReducer, INITIAL_STATE);
  const [meta, setMeta] = useState({ name: '', description: '', isDefault: false, logoData: null });
  const [saveStatus, setSaveStatus] = useState('saved');
  const [activeDrag, setActiveDrag] = useState(null);
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);
  const hasLoadedRef = useRef(false);

  const loadTemplate = useCallback(async () => {
    try {
      const res = await apiFetch(`/sow-templates/${id}`);
      if (!res.ok) { navigate('/sow-templates'); return; }
      const data = await res.json();
      setMeta({ name: data.name, description: data.description || '', isDefault: data.is_default, logoData: data.logo_data });
      if (data.template_json) dispatch({ type: 'SET_TEMPLATE', payload: data.template_json });
      hasLoadedRef.current = true;
    } catch (err) {
      console.error('Load SoW template error:', err);
    } finally {
      setLoading(false);
    }
  }, [id, apiFetch, navigate]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const save = useCallback(async () => {
    if (!hasLoadedRef.current) return;
    setSaveStatus('saving');
    try {
      await apiFetch(`/sow-templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: meta.name,
          description: meta.description,
          logoData: meta.logoData,
          isDefault: meta.isDefault,
          templateJson: template
        })
      });
      setSaveStatus('saved');
    } catch (err) {
      console.error('Save error:', err);
      setSaveStatus('error');
    }
  }, [id, meta, template, apiFetch]);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [template, meta, save]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event) => {
    const data = event.active.data?.current;
    if (data?.fromPalette) setActiveDrag({ label: data.label });
    else setActiveDrag(null);
  };

  const handleDragEnd = (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over) return;
    const sections = template.sections || [];
    if (active.data?.current?.fromPalette) {
      const { type, label } = active.data.current;
      if (over.id === 'empty-canvas') {
        dispatch({ type: 'ADD_SECTION', payload: { type, label } });
      } else {
        const dropIndex = sections.findIndex(s => s.id === over.id);
        dispatch({ type: 'ADD_SECTION', payload: { type, label, index: dropIndex !== -1 ? dropIndex + 1 : undefined } });
      }
      return;
    }
    if (active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      dispatch({ type: 'REORDER_SECTIONS', payload: arrayMove(sections, oldIndex, newIndex) });
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMeta(m => ({ ...m, logoData: reader.result }));
    reader.readAsDataURL(file);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading template...</div>;
  }

  const statusColors = { saved: '#22c55e', saving: '#f97316', unsaved: '#6b7280', error: '#ef4444' };
  const statusText = { saved: 'Saved', saving: 'Saving...', unsaved: 'Unsaved', error: 'Save failed' };

  return (
    <div style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', margin: '-24px', marginTop: '-16px' }}>
      {/* Top Bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', flexShrink: 0
      }}>
        <Button variant="ghost" onClick={() => navigate('/sow-templates')} style={{ padding: '6px 12px', fontSize: 13 }}>
          ← Back
        </Button>
        <Input value={meta.name} onChange={e => setMeta(m => ({ ...m, name: e.target.value }))}
          placeholder="Template Name" style={{ marginBottom: 0, flex: 1, maxWidth: 280 }} />
        <Input value={meta.description} onChange={e => setMeta(m => ({ ...m, description: e.target.value }))}
          placeholder="Description (optional)" style={{ marginBottom: 0, flex: 1, maxWidth: 320, fontSize: 12, color: 'var(--text-secondary)' }} />
        <span style={{ fontSize: 12, color: statusColors[saveStatus], fontWeight: 500 }}>{statusText[saveStatus]}</span>
        <Button onClick={save} style={{ padding: '6px 16px', fontSize: 13 }}>Save Now</Button>
      </div>

      {/* Three-Panel Layout */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Section Palette */}
        <div style={{
          width: 240, flexShrink: 0, borderRight: '1px solid var(--border)',
          padding: 12, overflowY: 'auto', background: 'var(--bg)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8 }}>
            SoW Sections
          </div>
          {SOW_SECTION_TYPES.map(s => (
            <PaletteItem key={s.type} type={s.type} label={s.label} icon={s.icon}
              onAdd={() => dispatch({ type: 'ADD_SECTION', payload: { type: s.type, label: s.label } })} />
          ))}
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8 }}>
              Logo
            </div>
            {meta.logoData ? (
              <div style={{ marginBottom: 8 }}>
                <img src={meta.logoData} alt="Logo" style={{ maxWidth: '100%', maxHeight: 60, borderRadius: 4 }} />
                <button onClick={() => setMeta(m => ({ ...m, logoData: null }))}
                  style={{ display: 'block', marginTop: 4, background: 'none', border: 'none', fontSize: 11, color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit' }}>
                  Remove logo
                </button>
              </div>
            ) : (
              <input type="file" accept="image/*" onChange={handleLogoUpload}
                style={{ width: '100%', fontSize: 11 }} />
            )}
          </div>
        </div>

        {/* Center: Section Editor */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--bg)' }}>
          <SortableContext items={(template.sections || []).map(s => s.id)} strategy={verticalListSortingStrategy}>
            {(template.sections || []).length === 0 ? (
              <div id="empty-canvas" style={{
                padding: 40, textAlign: 'center', border: '2px dashed var(--border)',
                borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13
              }}>
                Add sections from the palette on the left, or drag them here.
              </div>
            ) : (
              (template.sections || []).map(section => (
                <SortableSection key={section.id} section={section}
                  onUpdate={(sectionId, updates) => dispatch({ type: 'UPDATE_SECTION', payload: { id: sectionId, updates } })}
                  onRemove={sectionId => dispatch({ type: 'REMOVE_SECTION', payload: sectionId })} />
              ))
            )}
          </SortableContext>
        </div>

        {/* Right: Variable Reference */}
        <div style={{
          width: 260, flexShrink: 0, borderLeft: '1px solid var(--border)',
          padding: 12, overflowY: 'auto', background: 'var(--bg)'
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8 }}>
            Template Variables
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Use these in rich text sections. They are replaced with real data during generation.
          </div>
          {SOW_VARIABLES.map(v => (
            <div key={v.var} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '6px 8px', borderRadius: 4, marginBottom: 2,
              fontSize: 11, cursor: 'pointer',
              background: 'var(--bg-card)', border: '1px solid var(--border)',
            }} onClick={() => navigator.clipboard?.writeText(v.var)} title="Click to copy">
              <code style={{ color: 'var(--primary)', fontWeight: 600, fontSize: 10 }}>{v.var}</code>
              <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>{v.desc}</span>
            </div>
          ))}
        </div>
      </div>
      <DragOverlay>
        {activeDrag && (
          <div style={{
            padding: '8px 14px', background: 'var(--primary)', color: '#fff',
            borderRadius: 8, fontSize: 13, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'nowrap'
          }}>
            + {activeDrag.label}
          </div>
        )}
      </DragOverlay>
      </DndContext>
    </div>
  );
}

function PaletteItem({ type, label, icon, onAdd }) {
  const { attributes, listeners, setNodeRef } = useSortable({
    id: `palette-${type}`,
    data: { fromPalette: true, type, label },
  });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
        borderRadius: 6, marginBottom: 3, cursor: 'grab', fontSize: 12,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        transition: 'background 0.15s',
      }}
      onDoubleClick={onAdd}
    >
      <span style={{ fontSize: 13, width: 20, textAlign: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      <span style={{ fontSize: 16, color: 'var(--text-secondary)', cursor: 'pointer' }}
        onClick={(e) => { e.stopPropagation(); onAdd(); }}>+</span>
    </div>
  );
}
